import {
	GridRowModes,
	type GridRowId,
	type GridRowModesModel,
	type GridRowModel,
	type GridValidRowModel,
} from '@mui/x-data-grid-pro'
import { useCallback, useState } from 'react'

import type { GridApi } from './api/gridApi'
import type { GridConfig, GridInlineCreateResponse, GridInlineSaveResponse, GridRow } from './types'

const NEW_ROW_PREFIX = '__new__'

export const makeNewRowId = (): string => `${NEW_ROW_PREFIX}${Math.random().toString(36).slice(2, 10)}`

export const isNewRowId = (id: GridRowId): boolean => typeof id === 'string' && id.startsWith(NEW_ROW_PREFIX)

type Args = {
	config: GridConfig
	api: GridApi
	rows: GridRow[]
	setRows: (next: GridRow[]) => void
	onFlash?: (message: string, type: string | null) => void
}

export type RowUpdateError = { errors: Record<string, string>; flash?: string | null }

export const useGridEditing = ({ config, api, rows, setRows, onFlash }: Args) => {
	const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({})
	const [editErrors, setEditErrors] = useState<Record<string, Record<string, string>>>({})

	const startAdd = useCallback(() => {
		const id = makeNewRowId()
		const blank: GridRow = { [config.rowIdField]: id, isNew: true }
		setRows([blank, ...rows])
		setRowModesModel((m) => ({
			...m,
			[id]: { mode: GridRowModes.Edit, fieldToFocus: config.inlineEdit?.fields[0]?.name },
		}))
	}, [config, rows, setRows])

	const startEdit = useCallback((id: GridRowId) => {
		setRowModesModel((m) => ({ ...m, [id]: { mode: GridRowModes.Edit } }))
	}, [])

	const cancelEdit = useCallback(
		(id: GridRowId) => {
			if (isNewRowId(id)) {
				setRows(rows.filter((r) => r[config.rowIdField] !== id))
			}
			setRowModesModel((m) => ({ ...m, [id]: { mode: GridRowModes.View, ignoreModifications: true } }))
			setEditErrors(({ [String(id)]: _drop, ...rest }) => rest)
		},
		[config.rowIdField, rows, setRows],
	)

	const commitEdit = useCallback((id: GridRowId) => {
		setRowModesModel((m) => ({ ...m, [id]: { mode: GridRowModes.View } }))
	}, [])

	const processRowUpdate = useCallback(
		async (newRow: GridValidRowModel, _oldRow: GridValidRowModel): Promise<GridRowModel> => {
			const rowId = newRow[config.rowIdField] as string | number
			const { [config.rowIdField]: _drop, isNew: _isNew, ...values } = newRow as Record<string, unknown>

			const response: GridInlineSaveResponse | GridInlineCreateResponse = isNewRowId(rowId)
				? await api.inlineCreate(values)
				: await api.inlineSave(rowId, values)

			if (!response.success) {
				setEditErrors((prev) => ({ ...prev, [String(rowId)]: response.errors ?? {} }))
				if (response.flash !== null && response.flash !== undefined && onFlash !== undefined) {
					onFlash(response.flash, response.flashType ?? null)
				}
				const err: RowUpdateError = { errors: response.errors ?? {}, flash: response.flash ?? null }
				throw err
			}

			setEditErrors(({ [String(rowId)]: _drop2, ...rest }) => rest)
			if (response.flash !== null && response.flash !== undefined && onFlash !== undefined) {
				onFlash(response.flash, response.flashType ?? null)
			}
			return (response.row ?? newRow) as GridRowModel
		},
		[api, config.rowIdField, onFlash],
	)

	return {
		rowModesModel,
		setRowModesModel,
		startAdd,
		startEdit,
		cancelEdit,
		commitEdit,
		processRowUpdate,
		editErrors,
	}
}
